<template>
  <section v-if="isCurrentItemOwner && isMeeting && meetingInfo" class="meeting-details" data-testid="viewer-meeting-details">
    <template v-if="hasAttendees">
      <div class="meeting-details__actions">
        <Button
          type="solid"
          variant="blue"
          icon="social.mail"
          :text="allSent ? 'Sent to all' : 'Send to all attendees'"
          :loading="isSendingAll"
          :disabled="allSent"
          data-testid="meeting-send-all-btn"
          @click="onSendAll"
        />
      </div>
      <p class="meeting-details__label" data-testid="meeting-attendees-count">Attendees ({{ uniqueAttendees.length }})</p>
      <div class="meeting-details__attendees">
        <MeetingAttendeeRow
          v-for="attendee in uniqueAttendees"
          :key="attendee.email.toLowerCase()"
          :attendee="attendee"
          :sending="meetingShareStore.isSending(attendee.email)"
          :sent="meetingShareStore.isSent(attendee.email)"
          @send="onSend"
        />
      </div>
    </template>
    <p v-else class="meeting-details__empty" data-testid="meeting-no-attendees">There are no other attendees to send this recording to.</p>
  </section>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { Button } from '@/storybook';

import MeetingAttendeeRow from './MeetingAttendeeRow.vue';

import { useViewerItemStore } from '@/modules/viewer/store/useViewerItemStore';
import { useMeetingShareStore } from '@/modules/viewer/store/useMeetingShareStore';
import { useUserStore } from '@/store/useUserStore';
import { useUser } from '@/modules/viewer/composables/useUser';
import { useToast } from '@/composables/useToast';
import { DEBUG_NAMESPACE, useDebug } from '@/composables/useDebug';

const itemStore = useViewerItemStore();
const meetingShareStore = useMeetingShareStore();
const userStore = useUserStore();
const { isMeeting, meetingInfo, meetingAttendees } = storeToRefs(itemStore);
const { isCurrentItemOwner } = useUser();
const { success, error } = useToast();
const { debug } = useDebug(DEBUG_NAMESPACE.STORE_MEETING_SHARE);

const isSendingAll = ref(false);

// The owner is excluded — you can't send the recording to yourself.
const uniqueAttendees = computed(() => {
  const seen = new Set<string>();
  const ownerEmail = userStore.email?.toLowerCase();

  return meetingAttendees.value.filter(attendee => {
    const email = attendee.email ? attendee.email.toLowerCase() : '';

    if (!email || email === ownerEmail || seen.has(email)) {
      return false;
    }

    seen.add(email);
    return true;
  });
});

const hasAttendees = computed(() => uniqueAttendees.value.length > 0);

const allEmails = computed(() => uniqueAttendees.value.map(attendee => attendee.email.toLowerCase()));
const allSent = computed(() => hasAttendees.value && allEmails.value.every(email => meetingShareStore.isSent(email)));
const emailsToSend = computed(() => allEmails.value.filter(email => !meetingShareStore.isSent(email) && !meetingShareStore.isSending(email)));

const onSend = (email: string) => {
  const normalized = email.toLowerCase();

  // The row button stays clickable until the 'sending' flag re-renders it,
  // so a fast double-click would fire (and email) twice without this guard.
  if (meetingShareStore.isSending(normalized) || meetingShareStore.isSent(normalized)) {
    return;
  }

  meetingShareStore
    .share([normalized], 'individual')
    .then(() => {
      success(`Recording link sent to ${normalized}`);
    })
    .catch(e => {
      error('Could not send the recording link');
      debug('Cannot send meeting recording link', e);
    });
};

const onSendAll = () => {
  const emails = emailsToSend.value;

  if (!emails.length) {
    return;
  }

  isSendingAll.value = true;

  meetingShareStore
    .share(emails, 'send_all')
    .then(() => {
      success('Recording link sent to all attendees');
    })
    .catch(e => {
      error('Could not send the recording links');
      debug('Cannot send meeting recording links', e);
    })
    .finally(() => {
      isSendingAll.value = false;
    });
};
</script>

<style scoped>
.meeting-details {
  @apply flex flex-col;
}

.meeting-details__actions {
  @apply flex flex-wrap items-center gap-2 pb-3;
}

.meeting-details__label {
  @apply pb-1 text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400;
}

.meeting-details__attendees {
  @apply flex flex-col max-h-72 overflow-y-auto;
}

.meeting-details__empty {
  @apply py-2 text-sm text-zinc-500 dark:text-zinc-400;
}
</style>
